import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Link,
} from '@mui/material';
import {
  OpenInNew,
  CalendarToday,
  AccessTime,
} from '@mui/icons-material';
import ArticleTable from '../../components/Blog/ArticleTable';
import SEOHead from '../../components/SEO/SEOHead';
import PageHeader from '../../components/Layout/PageHeader';
import AtomicAnswer from '../../components/SEO/AtomicAnswer';
import FAQSection from '../../components/SEO/FAQSection';
import RelatedArticles from '../../components/Blog/RelatedArticles';
import NewsletterSection from '../../components/Layout/NewsletterSection';
import TrustStrip from '../../components/Layout/TrustStrip';
import { useTranslation } from 'react-i18next';

const getFaqs = (t) => [
  {
    question: t('blog.craVsRq.faq1Q'),
    answer: t('blog.craVsRq.faq1A'),
  },
  {
    question: t('blog.craVsRq.faq2Q'),
    answer: t('blog.craVsRq.faq2A'),
  },
  {
    question: t('blog.craVsRq.faq3Q'),
    answer: t('blog.craVsRq.faq3A'),
  },
  {
    question: t('blog.craVsRq.faq4Q'),
    answer: t('blog.craVsRq.faq4A'),
  },
  {
    question: t('blog.craVsRq.faq5Q'),
    answer: t('blog.craVsRq.faq5A'),
  },
];

const CRAvsRevenuQuebecPage = () => {
  const { t, i18n } = useTranslation();
  const { lng } = useParams();
  const faqs = getFaqs(t);
  return (
    <Box component="article" sx={{ bgcolor: '#F7F9FC', minHeight: '100vh' }}>
      <SEOHead
        title={t('blog.craVsRq.seoTitle')}
        description={t('blog.craVsRq.seoDescription')}
        canonical="/blog/cra-vs-revenu-quebec-diferencias"
        routeKey="blogCRAvsRQ"
        seoKey="blogCRAvsRQ"
        noIndex={i18n.language !== 'es'}
        ogImage="https://www.finmaple.com/images/blog/cra-revenu-quebec-impuestos.jpg"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: t('blog.craVsRq.seoTitle'),
          description: t('blog.craVsRq.seoDescription'),
          datePublished: '2026-02-22',
          dateModified: '2026-02-24',
          author: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
          publisher: {
            '@type': 'Organization',
            name: 'FinMaple',
            url: 'https://www.finmaple.com',
            logo: { '@type': 'ImageObject', url: 'https://www.finmaple.com/og-image.jpg' },
          },
          mainEntityOfPage: 'https://www.finmaple.com/blog/cra-vs-revenu-quebec-diferencias',
          speakable: {
            '@type': 'SpeakableSpecification',
            cssSelector: ['.article-headline', '.atomic-answer'],
          },
          inLanguage: i18n.language,
        }}
      />

      <PageHeader
        variant="functional"
        title={t('blog.craVsRq.pageTitle')}
        subtitle={t('blog.craVsRq.pageSubtitle')}
        breadcrumbs={[{ label: t('blog.craVsRq.breadcrumbGuides'), to: `/${lng}/blog` }, { label: 'Quebec', to: `/${lng}/quebec` }, { label: t('blog.craVsRq.breadcrumbCurrent') }]}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2.5, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#5F6B7A' }}>
            <CalendarToday sx={{ fontSize: 14 }} />
            <Typography variant="body2">{t('blog.craVsRq.date')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#5F6B7A' }}>
            <AccessTime sx={{ fontSize: 14 }} />
            <Typography variant="body2">{t('blog.craVsRq.readTime')}</Typography>
          </Box>
        </Box>
      </PageHeader>

      <TrustStrip />

      <Container maxWidth="md" sx={{ pb: 4 }}>
        <AtomicAnswer text={t('blog.craVsRq.atomicAnswer')} />

        {/* Why two declarations */}
        <Typography variant="h4" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t('blog.craVsRq.section1Title')}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t('blog.craVsRq.section1P1a')}{' '}<RouterLink to={`/${lng}/blog/guia-impuestos-latinos-quebec-2026`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t('blog.craVsRq.section1Link1')}</RouterLink>{' '}{t('blog.craVsRq.section1P1b')}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t('blog.craVsRq.section1P2')}
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
          <li><strong>T1 (federal)</strong> - {t('blog.craVsRq.section1Li1')}</li>
          <li><strong>TP-1 (provincial)</strong> - {t('blog.craVsRq.section1Li2')}</li>
        </Box>

        {/* Comparison Table */}
        <Typography variant="h4" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t('blog.craVsRq.tableTitle')}
        </Typography>
        <ArticleTable
          headerBg="#F7F9FC"
          columns={[
            { key: 'concept', label: t('blog.craVsRq.tableCol1'), headerSx: { color: '#1A1A1A' } },
            { key: 'cra', label: t('blog.craVsRq.tableCol2'), headerSx: { color: '#1E3A5F' } },
            { key: 'rq', label: t('blog.craVsRq.tableCol3'), headerSx: { color: '#6A1B9A' } },
          ]}
          rows={[
            { concept: t('blog.craVsRq.tableRow1Col1'), cra: 'T1 General', rq: 'TP-1' },
            { concept: t('blog.craVsRq.tableRow2Col1'), cra: 'T4', rq: 'RL-1' },
            { concept: t('blog.craVsRq.tableRow3Col1'), cra: t('blog.craVsRq.tableRow3Col2'), rq: t('blog.craVsRq.tableRow3Col3') },
            { concept: t('blog.craVsRq.tableRow4Col1'), cra: 'canada.ca/taxes', rq: 'revenuquebec.ca' },
            { concept: t('blog.craVsRq.tableRow5Col1'), cra: 'CRA My Account', rq: 'Mon dossier (clicSÉQUR)' },
            { concept: t('blog.craVsRq.tableRow6Col1'), cra: 'GST (5%)', rq: 'QST/TVQ (9.975%)' },
            { concept: t('blog.craVsRq.tableRow7Col1'), cra: 'GST/HST Credit', rq: 'Solidarity Tax Credit' },
            { concept: t('blog.craVsRq.tableRow8Col1'), cra: t('blog.craVsRq.tableRow8Col2'), rq: t('blog.craVsRq.tableRow8Col3') },
            { concept: t('blog.craVsRq.tableRow9Col1'), cra: t('blog.craVsRq.tableRow9Col2'), rq: t('blog.craVsRq.tableRow9Col3') },
          ]}
        />

        {/* Key Documents */}
        <Typography variant="h4" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t('blog.craVsRq.section2Title')}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t('blog.craVsRq.section2P1')}
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
          <li><strong>T4 + RL-1</strong> - {t('blog.craVsRq.section2Li1a')}{' '}<RouterLink to={`/${lng}/blog/t4-explicado-espanol-impuestos-canada`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t('blog.craVsRq.section2Link1')}</RouterLink></li>
          <li><strong>RL-31</strong> - {t('blog.craVsRq.section2Li2')}</li>
          <li><strong>T4A / RL-2</strong> - {t('blog.craVsRq.section2Li3')}</li>
          <li><strong>{t('blog.craVsRq.section2Li4Label')}</strong> - {t('blog.craVsRq.section2Li4')}</li>
          <li><strong>{t('blog.craVsRq.section2Li5Label')}</strong> - {t('blog.craVsRq.section2Li5')}</li>
        </Box>

        {/* Common Mistakes */}
        <Box
          sx={{
            bgcolor: '#FFFBF0',
            borderLeft: '4px solid #F9A825',
            borderRadius: '0 10px 10px 0',
            p: 3,
            my: 3,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1 }}>
            {t('blog.craVsRq.section3Title')}
          </Typography>
          <Box component="ol" sx={{ pl: 2.5, mb: 0, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li><strong>{t('blog.craVsRq.section3Li1Label')}</strong> - {t('blog.craVsRq.section3Li1')}</li>
            <li><strong>{t('blog.craVsRq.section3Li2Label')}</strong> - {t('blog.craVsRq.section3Li2')}</li>
            <li><strong>{t('blog.craVsRq.section3Li3Label')}</strong> - {t('blog.craVsRq.section3Li3a')}{' '}<RouterLink to={`/${lng}/blog/solidarity-tax-credit-quebec-guia-completa`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>Solidarity Tax Credit</RouterLink></li>
            <li><strong>{t('blog.craVsRq.section3Li4Label')}</strong> - {t('blog.craVsRq.section3Li4')}</li>
            <li><strong>{t('blog.craVsRq.section3Li5Label')}</strong> - {t('blog.craVsRq.section3Li5')}</li>
          </Box>
        </Box>


        {/* Quebec CTA */}
        <Box
          sx={{
            bgcolor: '#6A1B9A',
            borderRadius: '10px',
            p: 3,
            mt: 4,
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
              {t('blog.craVsRq.quebecCtaTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
              {t('blog.craVsRq.quebecCtaText')}
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to={`/${lng}/quebec`}
            variant="contained"
            sx={{
              bgcolor: '#fff',
              color: '#6A1B9A',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '14px',
              px: 3,
              flexShrink: 0,
              '&:hover': { bgcolor: '#F3E5F5' },
            }}
          >
            {t('blog.craVsRq.quebecCtaButton')}
          </Button>
        </Box>

        <RelatedArticles slug="cra-vs-revenu-quebec-diferencias" />

        {/* FAQs */}
        <FAQSection faqs={faqs} title={t('blog.craVsRq.faqTitle')} />

        {/* Official Sources */}
        <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #E6E9EE' }}>
          <Typography variant="subtitle2" sx={{ color: '#5F6B7A', fontWeight: 600, mb: 1.5 }}>
            {t('blog.craVsRq.sourcesTitle')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Link href="https://www.canada.ca/en/revenue-agency.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Canada Revenue Agency - canada.ca/taxes <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
            <Link href="https://www.revenuquebec.ca" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Revenu Quebec - revenuquebec.ca <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
          </Box>
          <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 2 }}>
            {t('blog.craVsRq.updatedNote')}
          </Typography>
        </Box>
      </Container>

      <NewsletterSection />
    </Box>
  );
};

export default CRAvsRevenuQuebecPage;
